import { emptyFragment, Select, SelectMenuItem } from '@execonline-inc/execonline-ui';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { assertNever } from '@kofno/piper';
import { fromNullable, Maybe } from 'maybeasy';
import * as React from 'react';
import { T } from '../../../../../Translations';
import CategoryBadge from './CategoryBadge';
import { CourseOption, RunChoice, runChoice } from './CourseOptions';
import StartDateChips from './StartDateChips';
import WhenItStarts, { RunChooser, startDate } from './WhenItStarts';

// What the card says about when the course starts. An email invite enrolls
// people in a run, so the card carries a run picker when there are several;
// a shared URL points at the course and the participant picks the run, so
// the card only states the choice (DVLP-8060).
export type RunColumn =
  | {
      kind: 'choose-run';
      selectedProgramId: Maybe<number>;
      onRunChange: (programId: number) => void;
    }
  | { kind: 'state-runs' };

// Who picks the run, for the wording on the card and in the catalog.
export const runChooser = (runs: RunColumn): RunChooser => {
  switch (runs.kind) {
    case 'choose-run':
      return 'admin';
    case 'state-runs':
      return 'participant';
    default:
      return assertNever(runs);
  }
};

interface Props {
  option: Maybe<CourseOption>;
  runs: RunColumn;
  catalogOpen: boolean;
  onToggleCatalog: () => void;
}

// The run picker sits inside the field; using it must not also open the catalog.
const keepToItself = (event: React.SyntheticEvent) => event.stopPropagation();

function RunPicker({
  choice,
  selectedProgramId,
  onRunChange,
}: {
  choice: RunChoice;
  selectedProgramId: Maybe<number>;
  onRunChange: (programId: number) => void;
}) {
  switch (choice.kind) {
    case 'start-anytime':
    case 'single-run':
      return <WhenItStarts choice={choice} chooser="admin" />;
    case 'several-runs': {
      const value = selectedProgramId.map(String).getOrElse(() => String(choice.runs[0].id));
      const renderValue = (selected: string) =>
        fromNullable(choice.runs.find((run) => String(run.id) === selected))
          .map((run) => startDate(run.startOn))
          .getOrElse(emptyFragment);
      return (
        <div className="flex items-center gap-2">
          <T kind="Starts" />
          <div
            className="w-44 cursor-default"
            onClick={keepToItself}
            onKeyDown={keepToItself}
            data-intercom-target="share-course-run-select"
          >
            <Select
              multiple={false}
              fullWidth
              value={value}
              onChange={(next) => onRunChange(parseInt(next, 10))}
              renderValue={renderValue}
            >
              {choice.runs.map((run) => (
                <SelectMenuItem
                  key={run.id}
                  value={String(run.id)}
                  selected={value === String(run.id)}
                >
                  {startDate(run.startOn)}
                </SelectMenuItem>
              ))}
            </Select>
          </div>
        </div>
      );
    }
    default:
      return assertNever(choice);
  }
}

// The run picker is a control and gets its own column at the right of the
// title row. The dates a shared URL offers go in a row of chips under that
// row instead, so the title keeps the width in the Share overlay's 600px
// dialog, and the "+N more" chip sits outside the card's button.
const cardParts = (
  course: CourseOption,
  runs: RunColumn,
): { column: React.ReactNode; below: React.ReactNode } => {
  switch (runs.kind) {
    case 'choose-run':
      return {
        column: (
          <div className="flex-none text-muted-foreground">
            <RunPicker
              choice={runChoice(course)}
              selectedProgramId={runs.selectedProgramId}
              onRunChange={runs.onRunChange}
            />
          </div>
        ),
        below: emptyFragment(),
      };
    case 'state-runs':
      // Keyed by course so the "+N more" expansion resets when another course is picked.
      return {
        column: emptyFragment(),
        below: <StartDateChips key={course.key} runs={course.runs} />,
      };
    default:
      return assertNever(runs);
  }
};

// The title row is the dropdown: clicking it, or Enter or Space on it, opens
// the catalog. The chosen course is laid out like a course header in the
// Builder (type, title, school, when it starts).
function SelectedCourse({ option, runs, catalogOpen, onToggleCatalog }: Props) {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggleCatalog();
    }
  };

  const parts = option.map((course) => cardParts(course, runs));

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border bg-card p-3 text-sm hover:border-primary focus-within:border-ring focus-within:ring-2 focus-within:ring-ring"
      data-intercom-target="share-selected-course"
    >
      <div
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={catalogOpen}
        onClick={onToggleCatalog}
        onKeyDown={handleKeyDown}
        className="flex cursor-pointer items-center gap-4 focus:outline-none"
        data-course-field-trigger
      >
        {option
          .map((course) => (
            <div key="course" className="flex min-w-0 flex-1 flex-wrap items-center gap-4">
              <CategoryBadge category={course.category} />
              <div className="min-w-0 flex-1">
                {/* Truncated text shows in full on hover, as the preview license selector does. */}
                <div
                  className="truncate"
                  style={{ fontWeight: 'var(--font-weight-semibold)' }}
                  title={course.family.name}
                >
                  {course.family.name}
                </div>
                {course.family.schoolName
                  .map((school) => (
                    <div
                      key="school"
                      className="truncate text-muted-foreground"
                      style={{ fontSize: 'var(--text-xs)' }}
                      title={school}
                    >
                      {school}
                    </div>
                  ))
                  .getOrElse(emptyFragment)}
              </div>
              {parts.map((p) => p.column).getOrElse(emptyFragment)}
            </div>
          ))
          .getOrElse(() => (
            // Same size and color as the participant box's placeholder below.
            <span className="flex-1 py-1 text-muted-foreground">
              <T kind="Select a course" />
            </span>
          ))}
        <ChevronDownIcon
          className={`h-5 w-5 flex-none text-muted-foreground transition-transform ${
            catalogOpen ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </div>
      {parts.map((p) => p.below).getOrElse(emptyFragment)}
    </div>
  );
}

export default SelectedCourse;
